import type {
    ServerCmsModelsPagesCategoryPageFacetValueGridPromo as FacetValueGridPromo,
    ServerCmsTypesFilterFacet as FilterFacet,
    GridPromoPlacements,
    GridPromoSnippet,
    ServerCmsTypesProductSummary as ProductSummary,
} from "@reactivated";

import { notEmpty } from "@thelabnyc/thelabui/src/utils/functional";

import type { SelectedFilters } from "./filterProducts";

interface TitleQualification {
    displayValue: string;
    displayOverride: string;
    facetSlug: string;
    valueSlug: string;
}

/**
 * When exactly one filter value is active on a `use_in_page_title` facet,
 * return its display value (e.g. "48 in") and the facet/value slugs for
 * canonical URL construction. Returns null if the state doesn't qualify.
 */
export const getTitleQualification = (
    filterFacets: FilterFacet[],
    selectedFilters: SelectedFilters,
): TitleQualification | null => {
    const activeValues = Object.values(selectedFilters).flat();
    if (activeValues.length !== 1) return null;

    return (
        filterFacets
            .filter((facet) => facet.use_in_page_title)
            .flatMap((facet) => {
                const selected = selectedFilters[facet.slug] ?? [];
                if (selected.length !== 1) return [];
                const value = facet.values.find((v) => v.slug === selected[0]);
                /* v8 ignore next -- @preserve */
                if (!value) return [];
                return [
                    {
                        displayValue: value.display_value,
                        displayOverride: value.display_override,
                        facetSlug: facet.slug,
                        valueSlug: value.slug,
                    },
                ];
            })[0] ?? null
    );
};

export const getQualifierLabel = (q: TitleQualification): string =>
    q.displayOverride || q.displayValue;

export const buildFilterURL = (
    url: string,
    selectedFilters: SelectedFilters,
    filterFacets: FilterFacet[],
): string => {
    const parsed = new URL(url, "http://localhost");
    const facetSlugs = new Set(filterFacets.map((f) => f.slug));
    const params = new URLSearchParams();
    for (const [key, value] of parsed.searchParams.entries()) {
        if (!facetSlugs.has(key)) {
            params.append(key, value);
        }
    }
    for (const [facetSlug, valueSlugs] of Object.entries(selectedFilters)) {
        for (const valueSlug of valueSlugs) {
            params.append(facetSlug, valueSlug);
        }
    }
    return params.size > 0 ? `${parsed.pathname}?${params}` : parsed.pathname;
};

export type SortOption = "featured" | "price-asc" | "price-desc";

export const sortProducts = (
    products: ProductSummary[],
    sortBy: SortOption,
): ProductSummary[] => {
    if (sortBy === "featured") return products;

    return [...products].sort((a, b) => {
        const priceA = a.msrp ? parseFloat(a.msrp.amount) : null;
        const priceB = b.msrp ? parseFloat(b.msrp.amount) : null;

        if (priceA === null && priceB === null) return 0;
        if (priceA === null) return 1;
        if (priceB === null) return -1;

        return sortBy === "price-asc" ? priceA - priceB : priceB - priceA;
    });
};

type GridItem =
    | { type: "product"; product: ProductSummary }
    | { type: "promo"; snippet: GridPromoSnippet; id: string };

export const interleavePromos = (
    products: ProductSummary[],
    promos: GridPromoPlacements,
): GridItem[] => {
    const typedProducts: GridItem[] = products.map((product) => ({
        type: "product",
        product,
    }));
    const sortedPromos = promos
        // Kill nulls and convert 1-based index to 0-based
        .map(({ id, value: { snippet, insert_index } }) =>
            snippet != null && insert_index != null
                ? {
                      id,
                      snippet,
                      insert_index: insert_index - 1,
                      type: "promo" as const,
                  }
                : null,
        )
        .filter(notEmpty)
        .sort((a, b) => a.insert_index - b.insert_index)
        // Adjust for grid slots occupied by earlier promos so that
        // insert_index means "absolute grid position," not "nth product"
        .map((promo, i) => ({
            ...promo,
            insert_index: promo.insert_index - i,
        }));

    const result = typedProducts
        .map((product, index) => {
            const promosAtIndex = sortedPromos.filter(
                (promo) => promo.insert_index === index,
            );
            return [...promosAtIndex, product];
        })
        .flat()
        // If an insert_index is way too big it just tacks on the end
        .concat(
            sortedPromos.filter(
                (promo) => promo.insert_index >= products.length,
            ),
        );

    return result;
};

/**
 * Choose what to render in the product grid based on filter state.
 * - 0 filters: page-level promos interleaved at their configured positions.
 * - 1 filter with an associated facet-value promo: that promo in slot 1, no
 *   page-level promos.
 * - Any other filtered state: products only.
 */
export const selectGridItems = (
    products: ProductSummary[],
    pagePromos: GridPromoPlacements,
    facetValuePromos: FacetValueGridPromo[],
    selectedFilters: SelectedFilters,
): GridItem[] => {
    const activeValues = Object.entries(selectedFilters).flatMap(
        ([facetSlug, valueSlugs]) =>
            valueSlugs.map((valueSlug) => [facetSlug, valueSlug] as const),
    );

    if (activeValues.length === 0) {
        return interleavePromos(products, pagePromos);
    }

    const typedProducts: GridItem[] = products.map((product) => ({
        type: "product",
        product,
    }));

    if (activeValues.length === 1) {
        const [facetSlug, valueSlug] = activeValues[0];
        const match = facetValuePromos.find(
            (fvp) =>
                fvp.facet_slug === facetSlug && fvp.value_slug === valueSlug,
        );
        if (match) {
            // The server-side Pick wraps `body` as a StreamField container;
            // unwrap to match the shape GridPromoCard expects.
            const snippet: GridPromoSnippet = {
                admin_name: match.snippet.admin_name,
                body: match.snippet.body.value,
            };
            return [
                {
                    type: "promo",
                    snippet,
                    id: `facet-promo-${facetSlug}-${valueSlug}`,
                },
                ...typedProducts,
            ];
        }
    }

    return typedProducts;
};
